import {
	CAMPAIGN_CONDITION_LABELS,
	CampaignConditionKind,
	CampaignConditionalValue,
	campaignConditionalValue,
} from "./campaign-conditions";
import { highestNodeNumber, keptNode, nodeIdAllocator, nodeNumber } from "./campaign-graph-nodes";
import { inviteStopsForAlreadyConnected, NEW_INVITE_STEP_DATA } from "./campaign-invite-stop";
import { layoutCampaignGraph } from "./campaign-layout";
import {
	CampaignConditionCheck,
	CampaignConditionStep,
	CampaignSequence,
	CampaignSequenceSendStep,
	CampaignSequenceStep,
} from "./campaign-sequence";

/**
 * The two converters between a campaign sequence (campaign-sequence.ts) and the builder's stored
 * graph, `campaignConfig[uuid].initialNodes` / `initialEdges` (PREC-4258). The graph they write is
 * what the builder writes: a starter `n1`, steps `n2…` joined by `normalEdge`, a condition's
 * branches by `yesEdge` / `noEdge`, positions from the builder's own layout. The builder lays the
 * graph out again when it opens it, so positions are never what decides how it reads.
 */

/** The data a stored node carries that the converters read or write. */
export interface CampaignGraphNodeData {
	id: number;
	label: string;
	level?: number;
	source?: string;
	edgeType?: string;
	nodeType?: string;
	days?: number | null;
	subject?: string;
	message?: string;
	alternateSubject?: string;
	alternateMessage?: string;
	premiumMessage?: string;
	alternatePremiumMessage?: string;
	senders?: { id: number | string }[];
	waitUntil?: boolean;
	conditionalValue?: CampaignConditionalValue;
	[key: string]: unknown;
}

export interface CampaignGraphNode {
	id: string;
	type?: string;
	position?: { x: number; y: number };
	draggable?: boolean;
	data: CampaignGraphNodeData;
	[key: string]: unknown;
}

export interface CampaignGraphEdge {
	id?: string;
	source: string;
	target: string;
	type?: string;
	sourceHandle?: string | null;
	[key: string]: unknown;
}

export interface CampaignGraph {
	initialNodes: CampaignGraphNode[];
	initialEdges: CampaignGraphEdge[];
}

export { CAMPAIGN_STEP_CONTENT_KEYS } from "./campaign-graph-nodes";

/** A stored graph the sequence cannot describe: the reason names the node. */
export class CampaignGraphReadError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "CampaignGraphReadError";
	}
}

const STARTER_NODE_TYPE = "starterNode";
const CONDITION_NODE_TYPE = "conditionalNode";

const SEND_STEP_NODE_TYPES: Readonly<Record<CampaignSequenceSendStep["type"], string>> = {
	email: "emailNode",
	linkedinInvite: "linkedinInviteNode",
	linkedinMessage: "linkedinNode",
	inmail: "linkedinInmailNode",
	linkedinVisit: "linkedinVisitNode",
	linkedinWithdrawInvite: "linkedinRemoveInviteNode",
};

const CHECK_KINDS: Readonly<Record<CampaignConditionCheck, CampaignConditionKind>> = {
	hasEmail: "leadHasEmail",
	inviteAccepted: "linkedinInviteNode",
	isConnected: "linkedinConnected",
	hasLinkedin: "leadHasLinkedin",
	emailOpened: "emailOpened",
	emailClicked: "emailClicked",
	leadPriority: "leadPriority",
};

// ============ Sequence → graph ============

/** The builder's start node, as `CampaignPage` creates it. */
function starterNode(): CampaignGraphNode {
	return {
		id: "n1",
		type: STARTER_NODE_TYPE,
		position: { x: 0, y: 0 },
		draggable: false,
		data: { id: 1, label: "Start", level: 0 },
	};
}

/** A step's pinned LinkedIn senders, as the builder stores them; none means all senders. */
function sendersOf(step: { senderUserIds?: number[] }): Pick<CampaignGraphNodeData, "senders"> {
	return step.senderUserIds?.length ? { senders: step.senderUserIds.map((id) => ({ id })) } : {};
}

/** The node fields a send step writes, under the names the builder and the scheduler read. */
function sendStepContent(step: CampaignSequenceSendStep): Partial<CampaignGraphNodeData> {
	switch (step.type) {
		case "email":
			return {
				subject: step.subject,
				message: step.body ?? "",
				alternateSubject: step.alternateSubject,
				alternateMessage: step.alternateBody,
			};
		case "linkedinInvite":
			return {
				message: step.note ?? "",
				premiumMessage: step.premiumNote,
				alternateMessage: step.alternateNote,
				alternatePremiumMessage: step.alternatePremiumNote,
				...sendersOf(step),
				// A kept step arrives here with its stored value filled in (campaign-sequence-edit),
				// so only a new step takes the new-step default (PREC-4426).
				stopIfAlreadyConnected: step.stopIfAlreadyConnected ?? NEW_INVITE_STEP_DATA.stopIfAlreadyConnected,
			};
		case "linkedinMessage":
			return { message: step.body ?? "", alternateMessage: step.alternateBody, ...sendersOf(step) };
		case "inmail":
			return {
				subject: step.subject,
				message: step.body ?? "",
				alternateSubject: step.alternateSubject,
				alternateMessage: step.alternateBody,
				...sendersOf(step),
			};
		case "linkedinVisit":
		case "linkedinWithdrawInvite":
			return sendersOf(step);
	}
}

/** The node fields a condition writes: its stored value and the wait-until-met switch. */
function conditionContent(step: CampaignConditionStep): Partial<CampaignGraphNodeData> {
	const conditionalValue = campaignConditionalValue(CHECK_KINDS[step.check]);

	if (step.check === "leadPriority" && step.priorities?.length) {
		conditionalValue.value = step.priorities.join(", ");
	}

	return { conditionalValue, ...(step.waitUntilMet ? { waitUntil: true } : {}) };
}

/** Drops the keys left undefined, as a JSON round trip of the builder's own node would. */
function withoutUndefined<T extends object>(value: T): T {
	return Object.fromEntries(
		Object.entries(value).filter(([, entry]) => entry !== undefined)
	) as T;
}

export interface CampaignSequenceToGraphOptions {
	/**
	 * The campaign's stored graph (PREC-4616). A step whose `id` names one of its nodes of the same
	 * type keeps that node; every other step gets a new node numbered above the stored maximum, as
	 * the builder's `highestNodeIdRef` numbers them, so no new step takes an id a schedule row still
	 * points at.
	 */
	stored?: CampaignGraph;
	/** The highest step number a schedule row of the campaign still points at (PREC-4616). */
	highestUsedNodeNumber?: number;
}

/**
 * The builder graph for a sequence: steps in pre-order, edges named `e<source>-<target>`, and the
 * builder's layout. Without a stored graph the nodes are `n1…` in creation order. Valid input is
 * the schema's job (`CampaignSequenceSchema`).
 */
export function campaignSequenceToGraph(
	sequence: CampaignSequence,
	{ stored, highestUsedNodeNumber }: CampaignSequenceToGraphOptions = {}
): CampaignGraph {
	const storedById = new Map((stored?.initialNodes ?? []).map((node) => [node.id, node]));
	const nodes: CampaignGraphNode[] = [starterNode()];
	const edges: CampaignGraphEdge[] = [];
	const used = new Set<string>();
	const nextId = nodeIdAllocator(
		highestNodeNumber(stored?.initialNodes ?? [], highestUsedNodeNumber) + 1
	);

	const idFor = (step: CampaignSequenceStep, type: string): string => {
		if (step.id && storedById.get(step.id)?.type === type && !used.has(step.id) && step.id !== "n1") {
			used.add(step.id);

			return step.id;
		}

		return nextId();
	};

	const addSteps = (
		steps: CampaignSequenceStep[],
		parentId: string,
		parentType: string,
		handle: string
	): void => {
		let previous = { id: parentId, type: parentType, handle };

		for (const step of steps) {
			const type = step.type === "condition" ? CONDITION_NODE_TYPE : SEND_STEP_NODE_TYPES[step.type];
			const id = idFor(step, type);
			const number = nodeNumber(id);
			const content = step.type === "condition" ? conditionContent(step) : sendStepContent(step);
			const fresh: CampaignGraphNode = {
				id,
				type,
				position: { x: 0, y: 0 },
				draggable: false,
				data: withoutUndefined({
					id: number,
					label: `Node ${id}`,
					source: previous.id,
					edgeType: previous.handle,
					nodeType: previous.type,
					days: step.delayDays,
					...content,
				}),
			};
			const storedNode = storedById.get(id);

			nodes.push(storedNode && used.has(id) ? keptNode(storedNode, fresh) : fresh);
			edges.push({
				id: `e${previous.id}-${id}`,
				source: previous.id,
				target: id,
				type: previous.handle,
				sourceHandle: previous.handle,
			});

			if (step.type === "condition") {
				addSteps(step.yes, id, type, "yesEdge");
				addSteps(step.no, id, type, "noEdge");

				return;
			}

			previous = { id, type, handle: "normalEdge" };
		}
	};

	addSteps(sequence.steps, "n1", STARTER_NODE_TYPE, "normalEdge");

	return { initialNodes: layoutCampaignGraph(nodes, edges), initialEdges: edges };
}

// ============ Graph → sequence ============

/** The handle an edge leaves its source by; an edge stored without one is a plain step edge. */
function handleOf(edge: CampaignGraphEdge): string {
	return edge.sourceHandle ?? edge.type ?? "normalEdge";
}

const SEND_STEP_TYPES_BY_NODE: ReadonlyMap<string, CampaignSequenceSendStep["type"]> = new Map(
	(Object.entries(SEND_STEP_NODE_TYPES) as [CampaignSequenceSendStep["type"], string][]).map(
		([stepType, nodeType]) => [nodeType, stepType]
	)
);

/** A step's pinned senders, read back; the builder's "All" entry means no pin. */
function senderUserIdsOf(data: CampaignGraphNodeData): { senderUserIds?: number[] } {
	const ids = (data.senders ?? [])
		.map((sender) => Number(sender.id))
		.filter((id) => Number.isInteger(id) && id > 0);

	return ids.length > 0 ? { senderUserIds: ids } : {};
}

/** A text field read back; the builder stores an unwritten body as "". */
function textOf(value: unknown): string | undefined {
	return typeof value === "string" && value !== "" ? value : undefined;
}

function sendStepOf(node: CampaignGraphNode, type: CampaignSequenceSendStep["type"]): CampaignSequenceSendStep {
	const { data } = node;
	const delayDays = Math.max(0, Math.trunc(Number(data.days ?? 0)) || 0);

	switch (type) {
		case "email":
			return withoutUndefined({
				type,
				delayDays,
				subject: textOf(data.subject),
				body: textOf(data.message),
				alternateSubject: textOf(data.alternateSubject),
				alternateBody: textOf(data.alternateMessage),
			});
		case "linkedinInvite":
			return withoutUndefined({
				type,
				delayDays,
				note: textOf(data.message),
				premiumNote: textOf(data.premiumMessage),
				alternateNote: textOf(data.alternateMessage),
				alternatePremiumNote: textOf(data.alternatePremiumMessage),
				...senderUserIdsOf(data),
				stopIfAlreadyConnected: inviteStopsForAlreadyConnected(data),
			});
		case "linkedinMessage":
			return withoutUndefined({
				type,
				delayDays,
				body: textOf(data.message),
				alternateBody: textOf(data.alternateMessage),
				...senderUserIdsOf(data),
			});
		case "inmail":
			return withoutUndefined({
				type,
				delayDays,
				subject: textOf(data.subject),
				body: textOf(data.message),
				alternateSubject: textOf(data.alternateSubject),
				alternateBody: textOf(data.alternateMessage),
				...senderUserIdsOf(data),
			});
		case "linkedinVisit":
		case "linkedinWithdrawInvite":
			return { type, delayDays, ...senderUserIdsOf(data) };
	}
}

/** The value a stored condition checks, read from its kind, else (older nodes) from its value. */
function checkOf(node: CampaignGraphNode): CampaignConditionCheck {
	const stored = node.data.conditionalValue;
	const byKind = (Object.keys(CHECK_KINDS) as CampaignConditionCheck[]).find(
		(check) => CHECK_KINDS[check] === stored?.currentType
	);

	if (byKind) {
		return byKind;
	}

	const byValue: Record<string, CampaignConditionCheck> = {
		[CAMPAIGN_CONDITION_LABELS.leadHasEmail.value]: "hasEmail",
		[CAMPAIGN_CONDITION_LABELS.linkedinInviteNode.value]: "inviteAccepted",
		[CAMPAIGN_CONDITION_LABELS.leadHasLinkedin.value]: "hasLinkedin",
		[CAMPAIGN_CONDITION_LABELS.emailOpened.value]: "emailOpened",
		[CAMPAIGN_CONDITION_LABELS.emailClicked.value]: "emailClicked",
	};
	// A condition saved before the picker stored a kind starts as "Lead has email", as the builder draws it.
	const check = byValue[stored?.value ?? CAMPAIGN_CONDITION_LABELS.leadHasEmail.value];

	if (!check) {
		throw new CampaignGraphReadError(
			`Condition ${node.id} checks "${stored?.title ?? stored?.value}", which a sequence cannot describe`
		);
	}

	return check;
}

/** The priorities a lead-priority condition lets through; none stored means it was never set. */
function prioritiesOf(node: CampaignGraphNode): { priorities?: string[] } {
	const value = node.data.conditionalValue?.value ?? "";
	const priorities = value
		.split(",")
		.map((priority) => priority.trim())
		.filter((priority) => priority !== "" && priority !== CAMPAIGN_CONDITION_LABELS.leadPriority.value);

	return priorities.length > 0 ? { priorities } : {};
}

interface GraphIndex {
	nodes: Map<string, CampaignGraphNode>;
	edgesFrom: Map<string, CampaignGraphEdge[]>;
}

function indexGraph(graph: CampaignGraph): GraphIndex {
	const nodes = new Map(graph.initialNodes.map((node) => [node.id, node]));
	const edgesFrom = new Map<string, CampaignGraphEdge[]>();

	for (const edge of graph.initialEdges) {
		if (!nodes.has(edge.source) || !nodes.has(edge.target) || edge.source === edge.target) {
			continue;
		}

		edgesFrom.set(edge.source, [...(edgesFrom.get(edge.source) ?? []), edge]);
	}

	return { nodes, edgesFrom };
}

/** The one node a source leads to by a handle; two is a graph the builder cannot draw either. */
function nextNode(index: GraphIndex, sourceId: string, handle: string): CampaignGraphNode | null {
	const targets = Array.from(
		new Set((index.edgesFrom.get(sourceId) ?? []).filter((edge) => handleOf(edge) === handle).map((edge) => edge.target))
	);

	if (targets.length > 1) {
		throw new CampaignGraphReadError(`Step ${sourceId} continues to more than one step by its ${handle}`);
	}

	return targets.length === 1 ? index.nodes.get(targets[0])! : null;
}

/**
 * The sequence a stored graph describes, read from its start node, with the node behind each step
 * in pre-order (a condition, then its yes list, then its no list). Nodes the start does not reach
 * (an orphan chain) are not part of the run and come back in `unreachableNodeIds`. Throws
 * `CampaignGraphReadError` for what the builder's tree cannot be: no start, a step reached twice,
 * a node type or condition the sequence does not know.
 */
export function campaignGraphToSequence(
	graph: CampaignGraph,
	{ includeStepIds = false }: { includeStepIds?: boolean } = {}
): {
	sequence: CampaignSequence;
	unreachableNodeIds: string[];
	/** The node of every step, in the order `campaignSequenceToGraph` numbers them (pre-order). */
	stepNodeIds: string[];
} {
	const index = indexGraph(graph);
	const starter = graph.initialNodes.find((node) => node.type === STARTER_NODE_TYPE);

	if (!starter) {
		throw new CampaignGraphReadError("The campaign has no start step");
	}

	const visited = new Set<string>([starter.id]);
	const stepNodeIds: string[] = [];

	const readSteps = (fromId: string, handle: string): CampaignSequenceStep[] => {
		const steps: CampaignSequenceStep[] = [];
		let node = nextNode(index, fromId, handle);

		while (node) {
			if (visited.has(node.id)) {
				throw new CampaignGraphReadError(`Step ${node.id} is reached by more than one path`);
			}

			visited.add(node.id);
			stepNodeIds.push(node.id);

			if (node.type === CONDITION_NODE_TYPE) {
				const conditionId: string = node.id;

				steps.push(
					withoutUndefined({
						...(includeStepIds && { id: conditionId }),
						type: "condition" as const,
						check: checkOf(node),
						delayDays: Math.max(0, Math.trunc(Number(node.data.days ?? 0)) || 0),
						waitUntilMet: node.data.waitUntil === true ? true : undefined,
						...(checkOf(node) === "leadPriority" ? prioritiesOf(node) : {}),
						yes: readSteps(conditionId, "yesEdge"),
						no: readSteps(conditionId, "noEdge"),
					})
				);

				return steps;
			}

			const stepType = SEND_STEP_TYPES_BY_NODE.get(node.type ?? "");

			if (!stepType) {
				throw new CampaignGraphReadError(`Step ${node.id} is a "${node.type}", which a sequence cannot describe`);
			}

			steps.push(includeStepIds ? { id: node.id, ...sendStepOf(node, stepType) } : sendStepOf(node, stepType));
			node = nextNode(index, node.id, "normalEdge");
		}

		return steps;
	};

	const steps = readSteps(starter.id, "normalEdge");
	const unreachableNodeIds = graph.initialNodes.map((node) => node.id).filter((id) => !visited.has(id));

	return { sequence: { steps }, unreachableNodeIds, stepNodeIds };
}
